import {
  coerceRegionalFormat,
  DEFAULT_CURRENCY,
  FLOW_MAX_GOALS_DEFAULT,
  isExpandedRepresentativeLine,
  pickT,
} from "@repo/shared";
import type { PublicFlowInstanceResponse, RegenerateSnapshotsResponse } from "@repo/shared/types";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";
import type { NavigateFunction } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ApiError, postWithToken } from "@/lib/api/_client";
import { useSessionToken } from "@/lib/context/public-session-context";
import { usePublicAnsweringSubjects } from "@/lib/hooks/use-public-flow/use-answering-subjects";
import { usePublicFlowCompletion } from "@/lib/hooks/use-public-flow/use-public-flow-completion";
import { usePublicFlowData } from "@/lib/hooks/use-public-flow/use-public-flow-data";
import { usePublicFlowSubmit } from "@/lib/hooks/use-public-flow/use-public-flow-submit";
import { usePublicSnapshotRegenerationWatch } from "@/lib/hooks/use-public-flow/use-public-snapshot-regeneration";
import { UserLanguageProvider } from "@/lib/i18n/context";
import { RegionalFormatProvider } from "@/lib/regional-format/context";
import { advisorReviewReasonText } from "./advisor-review-reason-text";
import { MemberSessionFlowView } from "./flow/member-session-flow-view";
import { GlobalPagesFlowBody } from "./global-pages-flow-body";
import { MultiGoalFlowBody } from "./multi-goal-flow-body";
import { PublicFlowCompletionView } from "./public-flow-completion-view";
import { T } from "./public-flow-content-inner.i18n";
import { usePublicFormValues, usePublicHasSuitabilityBlockers } from "./public-flow-context";
import { PublicFlowHeader } from "./public-flow-header";
import type { PublicPageRendererContext } from "./public-page-renderer";
import { SingleGoalFlowBody } from "./single-goal-flow-body";

export type FlowInstanceData = PublicFlowInstanceResponse | null;

export type PublicFlowContentInnerProps = {
  flowInstance: NonNullable<FlowInstanceData>;
  setFlowInstance: React.Dispatch<React.SetStateAction<FlowInstanceData>>;
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  mounted: boolean;
  navigate: NavigateFunction;
  /**
   * QL-505 — known synchronously from the `/p/m/:token/flow` route
   * (see `session/page.tsx`), so the member view can be chosen BEFORE the
   * `usePublicAnsweringSubjects` query resolves (or if it errors) instead of
   * briefly rendering the full advisor-style shell — which the backend would
   * deny almost every read/write of anyway.
   */
  isMemberSession?: boolean;
};

/**
 * Thin shell for the public flow (§3.11 split). Owns shared data + chrome
 * (header, completion view, suitability / snapshot alerts, Submit) and
 * dispatches the scrolling content to the single-goal or multi-goal body.
 * Must be rendered inside `PublicFlowProvider`.
 *
 * The header progress block was removed here (§3.11); it returns engine-driven
 * via Follow-up C P1.
 */
export function PublicFlowContentInner({
  flowInstance,
  setFlowInstance,
  saving,
  setSaving: _setSaving,
  mounted,
  navigate,
  isMemberSession = false,
}: PublicFlowContentInnerProps) {
  const sessionToken = useSessionToken();
  const storeFormValues = usePublicFormValues();
  const hasSuitabilityBlockers = usePublicHasSuitabilityBlockers();

  const data = usePublicFlowData(flowInstance, storeFormValues);

  // QL-505 — a member link (`/p/m/<token>`) session. `isMemberSession` (the
  // route prop) is the AUTHORITATIVE gate — it's known synchronously, before
  // any network round trip, so the wrong (full advisor-style) shell never
  // renders even momentarily. `usePublicAnsweringSubjects` still runs (the
  // backend confirms `sessionSubjectId` independently) purely to resolve the
  // display name for the "answering as" banner; `sessionSubjectId` is kept as
  // a defense-in-depth fallback in case a member session ever reaches this
  // component by a route other than `/p/m/:token/flow`.
  const { attribution: answeringAttribution, sessionSubjectId } = usePublicAnsweringSubjects();
  const isMemberView = isMemberSession || !!sessionSubjectId;
  const sessionMember =
    answeringAttribution?.kind === "members"
      ? answeringAttribution.members.find((m) => m.subjectId === sessionSubjectId)
      : undefined;
  const memberName = sessionMember?.name ?? "";

  // Snapshot regeneration (shell chrome). `usePublicFlowSubmit` flips this on a
  // 409 via `onSnapshotStale`.
  const [needsSnapshotRegeneration, setNeedsSnapshotRegeneration] = useState<boolean>(
    Boolean((flowInstance as any).needsSnapshotRegeneration),
  );
  // QL-675 — a rebuild queued or running in the API's worker (opening the
  // session queues one for a stale instance).
  const [snapshotsRegenerating, setSnapshotsRegenerating] = useState<boolean>(
    Boolean(flowInstance.snapshotsRegenerating),
  );
  const [isRefreshingSnapshots, setIsRefreshingSnapshots] = useState(false);
  const [snapshotRefreshError, setSnapshotRefreshError] = useState<string | null>(null);

  // Engine-driven completion/progress (Follow-up C). `completion` drives the
  // progress bar + D14 auto-clear (P1); `markers` + showValidationErrors drive
  // the per-tab / per-widget error highlighting (P2), threaded via the ctx.
  const { completion, markers } = usePublicFlowCompletion();

  const submit = usePublicFlowSubmit({
    clientName: data.client?.name,
    flowLang: data.flowLang,
    navigate,
    // QL-675 — the refused submit queued a rebuild; follow it.
    onSnapshotStale: () => {
      setNeedsSnapshotRegeneration(true);
      setSnapshotsRegenerating(true);
    },
    onPreviewBlocked: () =>
      setFlowInstance((prev) => (prev ? { ...prev, submitBlockedByPreview: true } : prev)),
    completion,
  });

  // This shell renders the UserLanguageProvider itself, so `useT` here would
  // read the outer (pre-flow) language — pick the flow language directly.
  const t = pickT(T, data.flowLang);

  // QL-314 — goals blocked by an advisor-only compliance component (engine
  // `kind: "advisor_review"`). These entries carry no pageId, so no page dot or
  // page banner can point at them; a public flow made of global pages only
  // would otherwise refuse Submit with no explanation. Same D14 gating as every
  // other engine gap: appears after a Submit attempt, never disables the button.
  // One line per goal AND reason: both compliance charts can hold a goal at once.
  // Deduped by TEXT, not reason: the four not-evaluable reasons share one line,
  // and a goal held by both charts would otherwise print it twice (with a
  // duplicate key).
  const advisorReviewGoals = (data.goals as Array<{ id: string; name?: string | null }>)
    .filter((g) => markers.missingAdvisorReviewGoalIds.has(g.id))
    .flatMap((g) => {
      const reasons = markers.advisorReviewReasons.get(g.id) ?? [];
      const texts = [
        ...new Set(
          reasons.length > 0
            ? reasons.map((reason) => advisorReviewReasonText(reason, data.flowLang))
            : [advisorReviewReasonText(undefined, data.flowLang)],
        ),
      ];
      return texts.map((text) => ({ id: `${g.id}:${text}`, name: g.name?.trim() || "", text }));
    });
  // QL-493 — a template gap (`kind: "config_error"`) holds the client's Submit
  // the same way; one generic line, since the client cannot act on it either.
  if (markers.configErrorReasons.length > 0) {
    advisorReviewGoals.push({ id: "template", name: "", text: t.advisorReviewTemplate });
  }

  // QL-675 — follow the worker's rebuild; when it ends, the hook refreshes
  // what it rewrote. A flag still set means it failed: the banner offers
  // Refresh again.
  usePublicSnapshotRegenerationWatch({
    instanceId: flowInstance.id,
    goalIds: (data.goals as Array<{ id: string }>).map((g) => g.id),
    regenerating: snapshotsRegenerating,
    onFinished: (status) => {
      setNeedsSnapshotRegeneration(status.needsSnapshotRegeneration);
      setSnapshotsRegenerating(false);
    },
  });

  // Refresh button: queues the rebuild and returns at once; the watch above
  // takes it from there.
  const handleRefreshSnapshots = async () => {
    setIsRefreshingSnapshots(true);
    setSnapshotRefreshError(null);
    try {
      const result = await postWithToken<RegenerateSnapshotsResponse>(
        "/public/flow/instance/regenerate-snapshots",
        sessionToken,
      );
      setNeedsSnapshotRegeneration(result.needsSnapshotRegeneration);
      setSnapshotsRegenerating(result.snapshotsRegenerating);
    } catch (err) {
      setSnapshotRefreshError(err instanceof ApiError ? err.message : t.snapshotsRefreshFailed);
    } finally {
      setIsRefreshingSnapshots(false);
    }
  };

  // Gate render until per-resource reads + product-types have arrived.
  if (!data.ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rendererCtx: PublicPageRendererContext = {
    formValues: data.formValues,
    goals: data.goals as any,
    fieldTypes: data.fieldTypes,
    isCompleted: data.isCompleted,
    flowInstanceId: flowInstance.id,
    riskModelId: (data.definition as any)?.riskModelId ?? null,
    portfolioCollectionId: (data.definition as any)?.portfolioCollectionId ?? null,
    flowCurrency: (data.definition as any)?.currency || DEFAULT_CURRENCY,
    multiPortfolio: (data.definition as any)?.multiPortfolio ?? false,
    primaryColor: (data.tenant as any)?.themePrimaryColor,
    productTypes: data.productTypes,
    goalTypes: (data.definition as any)?.portfolioCollection?.goalTypes ?? [],
    multiGoal: data.isMultiGoal,
    maxGoals: (data.definition as any)?.maxGoals ?? FLOW_MAX_GOALS_DEFAULT,
    dynamicPortfoliosEnabled:
      (data.definition as any)?.portfolioCollection?.dynamicPortfoliosEnabled === true,
    modelDeviationEnabled:
      (data.definition as any)?.portfolioCollection?.allowModelPortfolioRiskDeviation === true,
    portfolioCosts: data.portfolioCosts,
    chartColors: data.chartColors,
    clientType: data.clientType,
    clientName: data.client?.name ?? "",
    language: data.flowLang,
    showValidationErrors: submit.showValidationErrors,
    markers,
  };

  // QL-505 — a member-scoped session renders the stripped view and nothing
  // else of this shell (no header/progress/Submit/nav — the backend would
  // deny all of it anyway).
  if (isMemberView) {
    return (
      <UserLanguageProvider
        userLanguage={data.flowLang}
        tenantDefaultLanguage={data.flowLang}
        tenantSupportedLanguages={[data.flowLang]}
      >
        <RegionalFormatProvider regionalFormat={coerceRegionalFormat(data.tenant as any)}>
          <MemberSessionFlowView
            allPages={data.allPages}
            ctx={rendererCtx}
            memberName={memberName}
            memberRepresentative={sessionMember?.memberRepresentative}
            isExpandedLine={!!sessionMember && isExpandedRepresentativeLine(sessionMember)}
            language={data.flowLang}
            isRepresentative={
              answeringAttribution?.kind === "members" &&
              answeringAttribution.basis === "representatives"
            }
          />
        </RegionalFormatProvider>
      </UserLanguageProvider>
    );
  }

  return (
    <UserLanguageProvider
      userLanguage={data.flowLang}
      tenantDefaultLanguage={data.flowLang}
      tenantSupportedLanguages={[data.flowLang]}
    >
      {/* Regional formats come from the TENANT's stored settings — never the
          flow/template language (QL-108). */}
      <RegionalFormatProvider regionalFormat={coerceRegionalFormat(data.tenant as any)}>
        <div className="min-h-screen bg-background">
          {/* Sticky header (no progress block — §3.11). */}
          <div className="sticky top-0 z-10 bg-card border-b border-border shadow-sm">
            <PublicFlowHeader
              // Advisor-edited instance name wins over the template name —
              // same as the advisor header, minus its template subtitle
              // (user decision 2026-09-15).
              flowName={flowInstance.name || (data.definition as any)?.name}
              clientName={data.client?.name ?? ""}
              tenantLogoLightUrl={(data.tenant as any)?.logoLightUrl}
              tenantLogoDarkUrl={(data.tenant as any)?.logoDarkUrl}
              saving={saving}
              mounted={mounted}
              language={data.flowLang}
              isCompleting={submit.isCompleting}
              flowDescription={(data.definition as any)?.description}
              showSubmit={!submit.showCompletionView && !data.isCompleted}
              onSubmit={submit.handleSubmit}
              submitDisabled={hasSuitabilityBlockers || submit.isCompleting}
              submitBlockedByPreview={flowInstance.submitBlockedByPreview}
              showProgress={!submit.showCompletionView && !data.isCompleted}
              completionPercent={completion?.percent ?? 0}
              filledRequired={completion?.filledRequired ?? 0}
              totalRequired={completion?.totalRequired ?? 0}
            />
          </div>

          {submit.showCompletionView ? (
            <PublicFlowCompletionView
              clientName={data.client?.name}
              allowPdfGen={submit.allowPdfGen}
              allowSigning={submit.allowSigning}
              readyPdf={submit.readyPdf}
              onGeneratePdf={submit.handleGeneratePdf}
              onSign={submit.handleSign}
              onDone={submit.handleDone}
            />
          ) : (
            /* max-w-7xl: the followup custom-portfolio table carries ten
               columns and needs advisor-page room — 4xl forced a horizontal
               scroll on desktop clients (user report 2026-09-15). Matches
               the header cap. */
            <div className="max-w-7xl mx-auto px-4 py-8">
              {data.isCompleted && (
                <Alert variant="info" className="mb-6">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{t.formCompleted}</AlertDescription>
                </Alert>
              )}

              {/* QL-675 — the worker is rebuilding: nothing to click, just wait. */}
              {!data.isCompleted && snapshotsRegenerating && (
                <Alert variant="info" className="mb-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription>{t.snapshotsRegenerating}</AlertDescription>
                </Alert>
              )}

              {!data.isCompleted &&
                !snapshotsRegenerating &&
                (needsSnapshotRegeneration ||
                  (submit.showValidationErrors &&
                    (markers.missingSnapshotGoalIds.size > 0 ||
                      markers.missingInstanceSnapshotComponents.size > 0))) && (
                  <Alert variant="destructive" className="mb-6">
                    <RefreshCw className="h-4 w-4" />
                    <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        {snapshotRefreshError ??
                          (needsSnapshotRegeneration ? t.snapshotsStale : t.snapshotMissing)}
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRefreshSnapshots}
                        loading={isRefreshingSnapshots}
                      >
                        {t.refreshSnapshots}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

              {!data.isCompleted && hasSuitabilityBlockers && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{t.cannotSubmit}</AlertDescription>
                </Alert>
              )}

              {!data.isCompleted &&
                submit.showValidationErrors &&
                advisorReviewGoals.length > 0 && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p>{t.advisorReviewBlocked}</p>
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        {advisorReviewGoals.map((g) => (
                          <li key={g.id}>
                            {g.name ? <span className="font-medium">{g.name}: </span> : null}
                            {g.text}
                          </li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

              {!data.hasGoalPages ? (
                <GlobalPagesFlowBody allPages={data.allPages} ctx={rendererCtx} />
              ) : data.isMultiGoal ? (
                <MultiGoalFlowBody allPages={data.allPages} ctx={rendererCtx} />
              ) : (
                <SingleGoalFlowBody
                  allPages={data.allPages}
                  flowLang={data.flowLang}
                  ctx={rendererCtx}
                />
              )}
            </div>
          )}
        </div>
      </RegionalFormatProvider>
    </UserLanguageProvider>
  );
}
